import React, { useState, useEffect } from 'react';
import { Box, Typography, IconButton, Button, Checkbox, Tooltip } from '@mui/material';
import '@fancyapps/ui/dist/fancybox/fancybox.css';
import { Fancybox } from '@fancyapps/ui';
import { CheckedScreenIcon, CheckScreenIcon, CommentIcon, CursorIcon, DeleteIcon, IdleTimeIcon, KeyboardIcon, MoveActivitieIcon } from '../icons';
import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported';

import AddWorkNote from '../components/Projects/AddWorkNote';
import Skeleton from '@mui/material/Skeleton';


const RecentActivityCard = ({
  timeRange,
  user,
  task,
  image,
  progress,
  index,
  allImages,
  showProgress = true,
  showIndexCircle = true,
  isSelected = false,
  onSelect,
  selectMode = false,
  onRequestDeleteModal,
  onRequestMoveModal,
}) => {
  const [hovered, setHovered] = useState(false);
  const [localSelected, setLocalSelected] = useState(false);

  useEffect(() => {
    Fancybox.bind('[data-fancybox="gallery"]', {
      Carousel: {
        infinite: false,  // ye loop band karega
      },
    });

    return () => Fancybox.destroy();
  }, []);


  const hasImage = !!image && image.trim() !== '';
  const hasUserTask = user?.trim() !== '' && task?.trim() !== '';

  const isTrulyIdle =
    !hasImage &&
    (!progress || (progress.total === 0 && progress.cursor === 0 && progress.keyboard === 0));

  const getProgressColor = (total) => {
    if (total >= 1 && total <= 30) return '#FE5857';
    if (total >= 31 && total <= 49) return '#FFB82A';
    if (total >= 50) return '#0076FE';
    return '#48C774';
  };

  const handleCheckboxClick = (e) => {
    e.stopPropagation();
    if (onSelect) {
      onSelect();
    } else {
      setLocalSelected(!localSelected);
    }
  };

  const handleCardClick = () => {
    if (selectMode && onSelect) {
      onSelect();
    }
  };

  const selectionState = onSelect ? isSelected : localSelected;

  const [openAddWorkNote, setOpenAddWorkNote] = useState(false);

  const [loading, setLoading] = useState(true);
  setTimeout(() => {
    setLoading(false)
  }, 2000)

  return (
    <>
      {
        loading ? (
          <Box
            width="100%"
            borderRadius={3}
            bgcolor="#fff"
            p={2}
            sx={{ border: '1px solid #E5E8EB' }}
          >
            <Skeleton variant="text" width={80} height={20} sx={{ mb: 1 }} />

            <Skeleton
              variant="rectangular"
              height={140}
              sx={{ borderRadius: 2, mb: 2 }}
            />

            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Box>
                <Skeleton variant="text" width={100} height={20} />
                <Skeleton variant="text" width={140} height={16} />
              </Box>
              <Skeleton variant="circular" width={30} height={30} />
            </Box>

            <Box display="flex" alignItems="center" gap={1}>
              <Skeleton variant="rectangular" width="100%" height={6} />
              <Skeleton variant="text" width={30} height={16} />
              <Skeleton variant="circular" width={20} height={20} />
              <Skeleton variant="circular" width={20} height={20} />
            </Box>
          </Box>

        ) : (
          <Box
            className="hovsetupbg"
            width="100%"
            borderRadius={3}
            overflow="hidden"
            boxShadow={0}
            bgcolor="#fff"
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onClick={handleCardClick}
            sx={{
              transition: 'all 0.3s ease',
              border: selectionState ? '2px solid #0A84FF' : hovered ? '1px solid #0A84FF' : '1px solid #E5E8EB',
              position: 'relative',
              cursor: selectMode ? 'pointer' : 'default',
              transform: selectionState ? 'scale(0.98)' : 'scale(1)',
              '&:hover': {
                transform: selectMode ? 'scale(0.98)' : 'scale(1)',
                boxShadow: selectionState ? '0 4px 8px rgba(10, 132, 255, 0.3)' : '0 2px 4px rgba(0,0,0,0.1)',
              },
            }}
          >
            {/* Checkbox - visible in select mode or when hovered */}
            {(selectMode || hovered || selectionState) && (
              <Box sx={{
                position: 'absolute',
                top: '37px',
                left: '13px',
                zIndex: 2,
                backgroundColor: selectionState ? 'rgba(10, 132, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                borderRadius: '50%',
                width: 24,
                height: 24,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}>
                <Checkbox
                  checked={selectionState}
                  onChange={handleCheckboxClick}
                  icon={<CheckScreenIcon style={{width:'18px', height:'18px',}} />}
                  checkedIcon={<CheckedScreenIcon style={{width:'18px', height:'18px',}} />}
                  sx={{
                    padding: 0,
                    '& .MuiSvgIcon-root': {
                      fontSize: 25,
                    },
                  }}
                />


              </Box>
            )}

            {/* Time */}
            <Box px={2} pt={1} display="flex" justifyContent="space-between">
              <Typography sx={{fontSize:{xs:'10px', sm:'10px', xl:'12px'}}} color="#666">
                {timeRange}
              </Typography>
            </Box>

            {/* Image or Placeholder */}
            <Box
              className="hovsetup"
              sx={{
                position: 'relative',
                height: {xs:'140px', sm:'140px', md:'100px',lg:'100px', xl:'140px',},
                mt: 1,
                mx: 1,
                borderRadius: 2,
                overflow: 'hidden',
                backgroundColor: '#f5f5f5',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              {hasImage ? (
                <Box
                component="img"
                  src={image}
                  alt="screenshot"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: 10,
                    opacity: selectionState ? 0.7 : 1,
                    filter: selectionState ? 'brightness(0.9)' : 'brightness(1)',
                  }}
                />
              ) : (
                <Box display="flex" flexDirection="column" alignItems="center" sx={{ textAlign: 'center' }} gap={1}>
                  {isTrulyIdle ? (
                    <>
                      <IdleTimeIcon />
                      <Typography sx={{fontSize:{xs:'10px', sm:'10px', lg:'12px', xl:'14px'}}} color="#999" fontWeight={600}>
                        Idle Time
                      </Typography>
                    </>
                  ) : (
                    <>
                      <ImageNotSupportedIcon sx={{ color: '#999' }} />
                      <Typography sx={{fontSize:{xs:'10px', sm:'10px', lg:'12px', xl:'14px'}}} color="#999" fontWeight={600}>
                        No screenshot
                      </Typography>
                    </>
                  )}
                </Box>
              )}

              {/* Hover Overlay */}
              {hovered && (hasImage || hasUserTask) && !selectMode && (
                <>
                  {hasImage && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        bgcolor: 'rgba(0,0,0,0.6)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <a data-fancybox="gallery" href={image} style={{ textDecoration: 'none' }}>
                        <Button
                          variant="contained"
                          sx={{
                            bgcolor: 'white',
                            color: '#0A84FF',
                            fontWeight: 600,
                            borderRadius: 20,
                            px: 3,
                            py:0,
                            fontSize:{xs:'10px', lg:'12px', xl:'14px', },
                            '&:hover': { bgcolor: '#f0f0f0' },
                          }}
                        >
                          VIEW
                        </Button>
                      </a>

                      {allImages?.map((img, idx) =>
                        idx !== index && img && img.trim() !== '' ? (
                          <a key={idx} data-fancybox="gallery" href={img} style={{ display: 'none' }}>
                            hidden
                          </a>
                        ) : null
                      )}
                    </Box>
                  )}

                  <Box sx={{ position: 'absolute', top: 6, right: 6, display: 'flex', gap: 0.5 }}>
                    <Tooltip title="Refresh">
                      <IconButton size="small" className='sdjhsgdhgshdgsdgsjgd' sx={{ p: '2px' }} onClick={onRequestMoveModal}>
                        <MoveActivitieIcon style={{ color: 'white', width:'15px', height:'15px', fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" sx={{ p: '2px' }} onClick={onRequestDeleteModal}>
                        <DeleteIcon style={{ color: 'white', width:'15px', height:'15px', fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </>
              )}

              {/* Circle Label */}
              {showIndexCircle && hasImage && (
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 6,
                    right: 6,
                    bgcolor: 'white',
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    fontSize:{xs:'10px', sm:'10px', lg:'10px', xl:'12px'},
                    fontWeight: 700,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    boxShadow: 1,
                  }}
                >
                  {`${index + 1}`}
                </Box>
              )}
            </Box>

            {/* User & Task */}
            {(user || task) && (
              <Box px={2} pb={2} mt={1} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', }}>
                <Box>
                  {user && (
                    <Typography sx={{fontSize:{xs:'10px', sm:'10px', lg:'12px', xl:'14px'}}} fontWeight={600} color="#0A84FF">
                      {user}
                    </Typography>
                  )}
                  {task && (
                    <Typography sx={{fontSize:{xs:'10px', sm:'10px', lg:'10px', xl:'12px'}}} color="#888" noWrap>
                      {task}
                    </Typography>
                  )}
                </Box>
                {showProgress && progress && (<Tooltip title="Add Work Note" arrow placement="top"><Button onClick={() => setOpenAddWorkNote(true)} sx={{ minWidth: 'auto', padding: '3px 0' }}><CommentIcon /></Button></Tooltip>)}
              </Box>
            )}
            <AddWorkNote open={openAddWorkNote} onClose={() => setOpenAddWorkNote(false)} />
            {/* Progress Bar */}
            {showProgress && progress && (
              <Box px={2} pb={1}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Box
                    width="100%"
                    height={6}
                    borderRadius={3}
                    bgcolor="#eee"
                    display="flex"
                    overflow="hidden"
                  >
                    <Box
                      width={`${progress.total}%`}
                      bgcolor={getProgressColor(progress.total)}
                      sx={{ transition: 'background-color 0.3s ease' }}
                    />
                  </Box>
                  <Typography fontSize={12} color="#666">
                    {progress.total}%
                  </Typography>
                  <Typography
                    fontSize={12}
                    color="orange"
                    sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}
                  >
                    <Tooltip title="Mouse  Activity" arrow placement="top" style={{ display: 'flex', alignItems: 'center', gap: '1px' }}><CursorIcon /> {progress.cursor}%</Tooltip>
                  </Typography>
                  <Typography
                    // fontSize={12}
                    color="green"
                    sx={{ display: 'flex', alignItems: 'center', gap: '2px',fontSize:{xs:'10px', sm:'10px', lg:'10px', xl:'12px'} }}
                  >
                    <Tooltip title="Keyboard Activity" arrow placement="top" style={{ display: 'flex', alignItems: 'center', gap: '1px' }}> <KeyboardIcon /> {progress.keyboard}%</Tooltip>
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        )
      }

    </>
  );
};

export default RecentActivityCard;
